import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import { COMPANY, DESIGN_PARTNERS, MANAGEMENT_TEAM, ADVISORS } from "../data/mock";
import { FadeIn, SplitText, SpotlightCard, TiltCard, HexPattern, Eyebrow } from "../components/reactbits/Anim";

const cardCls =
  "rounded-2xl border border-[#EDE5D3] bg-white shadow-[0_2px_16px_rgba(31,27,19,0.04)] transition-all duration-300 hover:border-[#F0A322]/50 hover:shadow-[0_8px_30px_rgba(232,147,12,0.12)]";

/* Uniform, aligned portrait frame */
const Avatar = ({ person }) => (
  <div className="mx-auto h-44 w-44 overflow-hidden rounded-2xl border border-[#EDE5D3] bg-[#FAF6EE]">
    {person.photo ? (
      <img
        src={person.photo}
        alt={person.name}
        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
      />
    ) : (
      <div className="flex h-full w-full items-center justify-center bg-[#F0A322]/[0.14]">
        <span className="font-heading text-4xl font-bold text-[#B45309]">{person.initials}</span>
      </div>
    )}
  </div>
);

const PersonCard = ({ person, testid }) => (
  <TiltCard className="h-full">
    <SpotlightCard className={`group h-full ${cardCls} p-8 text-center`}>
      <div data-testid={testid} className="flex h-full flex-col items-center">
        <Avatar person={person} />
        <h3 className="mt-6 font-heading text-lg font-semibold leading-snug text-[#1F1B13]">{person.name}</h3>
        <p className="mt-1.5 text-sm font-semibold leading-snug text-[#B45309]">{person.title}</p>
        {person.experience && (
          <p className="mt-2.5 text-xs italic leading-relaxed text-[#8A8272]" data-testid="person-experience">
            {person.experience}
          </p>
        )}
        <div className="flex-1" />
        {person.linkedin && (
          <a
            href={person.linkedin}
            target="_blank"
            rel="noreferrer"
            aria-label={`${person.name} LinkedIn`}
            className="mt-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#E5DCC9] text-[#8A8272] transition-colors duration-200 hover:border-[#E8930C] hover:text-[#B45309]"
          >
            <Linkedin className="h-4 w-4" />
          </a>
        )}
      </div>
    </SpotlightCard>
  </TiltCard>
);

const Company = () => (
  <div className="bg-white text-[#1F1B13]" data-testid="company-page">
    {/* ===== Page hero ===== */}
    <section className="relative overflow-hidden bg-[#FDFBF7] pt-40 pb-20">
      <HexPattern opacity={0.07} />
      <div
        className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[760px] -translate-x-1/2 rounded-full"
        style={{ background: "radial-gradient(ellipse at center, rgba(240,163,34,0.13), transparent 60%)" }}
      />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <Eyebrow>{COMPANY.aboutEyebrow}</Eyebrow>
        <h1 className="mt-6 max-w-3xl font-heading text-4xl font-bold leading-tight text-[#1F1B13] sm:text-6xl" data-testid="company-heading">
          <SplitText text="Driving the future of" />{" "}
          <span className="text-[#E8930C]">
            <SplitText text="medical device innovation" delay={0.3} />
          </span>
        </h1>
      </div>
    </section>

    {/* ===== About copy ===== */}
    <section className="py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-2">
          <FadeIn>
            <p className="text-base leading-relaxed text-[#5D564A] sm:text-lg">{COMPANY.aboutBody1}</p>
          </FadeIn>
          <FadeIn delay={0.12}>
            <div className="rounded-2xl border border-[#F0A322]/30 bg-[#F0A322]/[0.07] p-8">
              <Sparkles className="h-7 w-7 text-[#B45309]" />
              <p className="mt-4 text-lg font-medium leading-relaxed text-[#37311F]">{COMPANY.aboutBody2}</p>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>

    {/* ===== Leadership Team ===== */}
    <section className="relative overflow-hidden bg-[#FAF6EE] py-24 lg:py-32" id="team">
      <HexPattern opacity={0.04} />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <Eyebrow>{COMPANY.teamEyebrow}</Eyebrow>
          <h2 className="mt-6 font-heading text-3xl font-bold text-[#1F1B13] sm:text-5xl" data-testid="team-heading">
            THE <span className="text-[#E8930C]">LEADERSHIP</span> TEAM
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#5D564A]">{COMPANY.teamBody}</p>
        </FadeIn>

        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="team-grid">
          {MANAGEMENT_TEAM.map((p, i) => (
            <FadeIn key={p.name} delay={(i % 3) * 0.1} className="h-full">
              <PersonCard person={p} testid={`team-member-${i}`} />
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    {/* ===== Advisors ===== */}
    <section className="py-24 lg:py-32" id="advisors">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <Eyebrow>advisors</Eyebrow>
          <h2 className="mt-6 font-heading text-3xl font-bold text-[#1F1B13] sm:text-5xl" data-testid="advisors-heading">
            OUR <span className="text-[#E8930C]">ADVISORS</span>
          </h2>
        </FadeIn>

        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="advisors-grid">
          {ADVISORS.map((p, i) => (
            <FadeIn key={p.name} delay={(i % 4) * 0.1} className="h-full">
              <PersonCard person={p} testid={`advisor-${i}`} />
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    {/* ===== Design Partners ===== */}
    <section className="relative overflow-hidden bg-[#FAF6EE] py-24 lg:py-32" id="design-partners">
      <HexPattern opacity={0.04} />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <Eyebrow>{DESIGN_PARTNERS.eyebrow}</Eyebrow>
          <h2 className="mt-6 font-heading text-3xl font-bold text-[#1F1B13] sm:text-5xl" data-testid="design-partners-heading">
            Now selecting <span className="text-[#E8930C]">design partners</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#5D564A]">{DESIGN_PARTNERS.body}</p>
        </FadeIn>

        <div className="mx-auto mt-12 max-w-2xl" data-testid="design-partners-bullets">
          <ul className="space-y-3">
            {DESIGN_PARTNERS.bullets.map((b) => (
              <FadeIn key={b}>
                <li className="flex items-start gap-3 rounded-xl border border-[#EDE5D3] bg-white px-5 py-4 shadow-[0_2px_8px_rgba(31,27,19,0.03)]">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#E8930C]" />
                  <span className="text-sm text-[#4A4436]">{b}</span>
                </li>
              </FadeIn>
            ))}
          </ul>
          <FadeIn delay={0.1}>
            <div className="mt-8 text-center">
              <Link
                to="/contact-us"
                data-testid="design-partners-cta"
                className="group inline-flex items-center gap-2.5 rounded-full bg-[#F0A322] px-8 py-4 text-sm font-semibold text-[#231A07] transition-colors duration-200 hover:bg-[#E8930C]"
              >
                {DESIGN_PARTNERS.cta}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>

    {/* ===== Careers ===== */}
    <section className="relative overflow-hidden">
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(135deg, rgba(240,163,34,0.16) 0%, rgba(240,163,34,0.04) 55%, transparent 100%)" }}
      />
      <HexPattern opacity={0.06} />
      <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
        <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <div>
            <Eyebrow>{COMPANY.careersEyebrow}</Eyebrow>
            <h2 className="mt-5 font-heading text-3xl font-bold text-[#1F1B13] sm:text-4xl" data-testid="careers-heading">
              JOIN OUR <span className="text-[#E8930C]">TEAM</span>
            </h2>
            <p className="mt-3 max-w-xl text-[#5D564A]">{COMPANY.careersBody}</p>
          </div>
          <Link
            to="/contact-us"
            data-testid="careers-cta"
            className="group inline-flex items-center gap-2.5 rounded-full bg-[#F0A322] px-8 py-4 text-sm font-semibold text-[#231A07] transition-colors duration-200 hover:bg-[#E8930C]"
          >
            {COMPANY.careersCta}
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default Company;
